import Loader from 'components/Loader';
import React, { useState } from 'react';
import { callerIdService } from 'services/caller-id';
import { formatPhone } from 'utils/formatPhone';
import { CloseIcon, UserPlusIcon } from './icons';
import { CallerRef } from './use-caller-id';

interface AddCustomerSheetProps {
  caller: CallerRef; // unknown caller whose Book triggered this sheet
  onCreated: (customerId: number, fullName: string) => void;
  onClose: () => void;
  onToast: (msg: string) => void;
}

const GENDERS = [
  { key: '', label: 'None' },
  { key: 'Female', label: 'Female' },
  { key: 'Male', label: 'Male' },
];

// "Add Customer" bottom sheet — collects customer info for an unknown
// caller before continuing to the booking page (design_handoff_add_customer).
// Mounted fresh per open, so field state resets automatically.
const AddCustomerSheet = ({ caller, onCreated, onClose, onToast }: AddCustomerSheetProps) => {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  // displayed formatted; submit strips back to digits for the API
  const [phone, setPhone] = useState(String(formatPhone(caller.phone) ?? ''));
  const [email, setEmail] = useState('');
  const [bday, setBday] = useState('');
  const [gender, setGender] = useState('');
  const [saving, setSaving] = useState(false);

  const canSubmit = first.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return onToast('Enter the customer name before adding.');
    if (saving) return;
    setSaving(true);
    const fullName = `${first.trim()} ${last.trim()}`.trim();
    callerIdService.createCustomer({
      firstName: first.trim(),
      lastName: last.trim(),
      phone: phone.replace(/[^0-9]/g, ''),
      email: email.trim(),
      birthday: bday.trim(),
      gender,
    })
      .then(id => onCreated(id, fullName))
      .catch(() => onToast('Failed to add customer, please try again.'))
      .finally(() => setSaving(false));
  };

  return (
    <>
      {saving && <Loader />}
      <div className="cid-addcust-scrim" onClick={onClose} />
      <div className="cid-addcust-sheet">
        <div className="cid-sheet-grab"><span /></div>
        <div className="cid-subsheet-header">
          <span className="cid-addcust-icon"><UserPlusIcon size={16} /></span>
          <span className="cid-subsheet-title">Add Customer</span>
          <button type="button" className="cid-sheet-close" onClick={onClose}><CloseIcon size={17} /></button>
        </div>

        <div className="cid-addcust-form">
          <label className="cid-field">
            <span className="cid-field-label"><em>*</em>First name</span>
            <input value={first} onChange={e => setFirst(e.target.value)} placeholder="WALK-IN" />
          </label>
          <label className="cid-field">
            <span className="cid-field-label">Last name</span>
            <input value={last} onChange={e => setLast(e.target.value)} placeholder="Enter last name" />
          </label>
          <label className="cid-field">
            <span className="cid-field-label"><em>*</em>Phone number</span>
            <input className="prefilled" value={phone} onChange={e => setPhone(e.target.value)} />
          </label>
          <label className="cid-field">
            <span className="cid-field-label">Email</span>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email" />
          </label>
          <label className="cid-field">
            <span className="cid-field-label">Birthday</span>
            <input value={bday} onChange={e => setBday(e.target.value)} placeholder="MM/DD" />
          </label>
          <div className="cid-field">
            <span className="cid-field-label">Gender</span>
            <div className="cid-gender-chips">
              {GENDERS.map(g => (
                <button
                  key={g.label}
                  type="button"
                  className={`cid-gender-chip ${gender === g.key ? 'selected' : ''}`}
                  onClick={() => setGender(g.key)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="cid-addcust-helper">
          The customer will be saved to your contacts and linked to the incoming number. After adding, you continue booking for this customer.
        </div>

        <div className="cid-addcust-footer">
          <button type="button" className="cid-btn-outline" onClick={onClose}>Close</button>
          <button type="button" className={`cid-btn-confirm ${canSubmit ? '' : 'disabled'}`} onClick={submit}>
            Add &amp; book
          </button>
        </div>
      </div>
    </>
  );
};

export default AddCustomerSheet;
